import React, { useMemo } from 'react';
import * as Yup from "yup";

import { FieldTypes } from '../FormLogs';

export const useCustomForm = (form) => {
    /**
     * @deprecated We no longer need to pre-initialise the state of the custom form
     * @returns {{}}
     */
    const generateBlankObject = () => {
        let obj = {};
        form?.fields.forEach(field => {
            if ([FieldTypes.TYPE_CHECKBOX, FieldTypes.TYPE_T_AND_C].includes(field.type)) {
                obj[field.id] = false;
            } else if(field.type !== FieldTypes.TYPE_CONTENT) {
                obj[field.id] = '';
            }
        });
        return obj;
    }

    const generateValidationSchema = (fields, depth = 0, parentField = null) => {
        let schema = {};

        if (!fields) {
            return schema;
        }

        fields.forEach(field => {
            const fieldIndex = field.id;

            // Skip content fields as they never need validation
            if (field.type === FieldTypes.TYPE_CONTENT) {
                return;
            }

            // Handle file uploads
            if (field.type === FieldTypes.TYPE_FILEUPLOAD) {
                schema[fieldIndex] = Yup.array();

                if (field.validations === 'required') {
                    if (parentField) {
                        schema[fieldIndex] = schema[fieldIndex].when(parentField, {
                            is: true,
                            then: (schema) => schema.min(1, `${field.label} is required`),
                            otherwise: (schema) => schema
                        });
                    } else {
                        schema[fieldIndex] = schema[fieldIndex].min(1, `${field.label} is required`);
                    }
                }

                return;
            }

            // Handle checkboxes and T&Cs
            if ([FieldTypes.TYPE_CHECKBOX, FieldTypes.TYPE_T_AND_C].includes(field.type)) {
                schema[fieldIndex] = Yup.boolean().nullable();

                // Make required if needed
                if (field.type === FieldTypes.TYPE_T_AND_C
                    || (field.type === FieldTypes.TYPE_CHECKBOX && field.validations === 'required'))
                {
                    if (parentField) {
                        schema[fieldIndex] = schema[fieldIndex].when(parentField, {
                            is: true,
                            then: (schema) => schema.oneOf([true], `Please accept ${field.label}`),
                            otherwise: (schema) => schema
                        });
                    } else {
                        schema[fieldIndex] = schema[fieldIndex].oneOf([true], `Please accept ${field.label}`);
                    }
                }

                // Process subfields if they exist
                if (field.subFields) {
                    const subSchema = generateValidationSchema(field.subFields, depth + 1, fieldIndex);
                    schema = { ...schema, ...subSchema };
                }

                return;
            }

            // Handle all other field types (text, select, etc.)
            schema[fieldIndex] = Yup.string().nullable();

            // Apply required validation if needed
            if (field.validations === 'required') {
                if (parentField) {
                    schema[fieldIndex] = schema[fieldIndex].when(parentField, {
                        is: true,
                        then: (schema) => schema.required(`${field.label} is required`),
                        otherwise: (schema) => schema
                    });
                } else {
                    schema[fieldIndex] = schema[fieldIndex].required(`${field.label} is required`);
                }
            }
        });

        return schema;
    }

    const schemaShape = useMemo(() => generateValidationSchema(form?.fields), [form?.id]);

    return {
        schema: Yup.object().shape(schemaShape).nullable(),
        initialState: generateBlankObject(),
    }
}
